import { useState } from 'react'
import { Battery, CircleDot, Compass, Gauge, Radio, Satellite, Upload } from 'lucide-react'
import { Button, Field, Metric, Divider } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { REMOTE_ID } from '../../lib/classes'
import { fmtAlt, fmtPct, fmtSpeed } from '../../lib/format'
import type { MavlinkTelemetry } from '../../types'

/* ============================================================================
 * ПАНЕЛЬ ИДЕНТИФИКАЦИИ И ТЕЛЕМЕТРИИ
 *
 * ЭРА-ГЛОНАСС Remote ID:
 *   • частота 1090 МГц
 *   • мощность излучения не менее 2 Вт
 *   • координаты в СК WGS-84
 *   • формат обмена данными — ГОСТ Р 71934-2025
 *
 * Телеметрия MAVLink поступает с /api/mavlink. Если БВС не подключён, запрос
 * не отвечает — таймаут обрабатывается, панель показывает деградацию.
 * ========================================================================= */

export function RemoteIdPanel({
  telemetry,
  connected,
  onRefresh,
}: {
  telemetry: MavlinkTelemetry | null
  connected: boolean
  onRefresh: () => void
}) {
  // Имя выбранного файла сертификата (аналог регистрации Part 107)
  const [certFile, setCertFile] = useState<string | null>(null)

  return (
    <div className="space-y-2 p-2.5">
      {/* Статус Remote ID */}
      <div
        className={`rounded-md border px-2.5 py-2 ut-stated ${connected ? 'ut-state-approved' : 'ut-state-pending'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <Radio size={12} aria-hidden />
            {REMOTE_ID.standard} · Remote ID
          </div>
          <StateBadge state={connected ? 'approved' : 'pending'}>
            {connected ? 'Передача идёт' : 'Ожидание БВС'}
          </StateBadge>
        </div>
        <div className="ut-metric mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-muted">
          <span>Диапазон: {REMOTE_ID.frequencyMHz} МГц</span>
          <span>Мощность: ≥ {REMOTE_ID.minPowerW} Вт</span>
          <span>СК: {REMOTE_ID.crs}</span>
        </div>
      </div>

      <Divider label="Телеметрия MAVLink" />

      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Высота"
          value={fmtAlt(telemetry?.alt_m)}
          icon={Gauge}
          state={connected ? 'active' : 'pending'}
          title="Высота над уровнем земли, метры"
        />
        <Metric label="Отн. высота" value={fmtAlt(telemetry?.rel_alt_m)} icon={Compass} />
        <Metric label="Скорость" value={fmtSpeed(telemetry?.speed_ms)} icon={CircleDot} />
        <Metric
          label="Курс"
          value={telemetry?.heading_deg !== undefined ? `${telemetry.heading_deg.toFixed(0)}°` : '—'}
          icon={Compass}
        />
        <Metric label="Заряд" value={fmtPct(telemetry?.battery_pct)} icon={Battery} />
        <Metric label="Режим" value={telemetry?.mode ?? '—'} icon={Satellite} />
      </div>

      <div className="ut-metric flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-2 py-1.5 text-[10px] text-ink-muted">
        <span>Идентификатор БВС: {telemetry?.uas_id ?? '—'}</span>
        <span>Вооружение: {telemetry?.armed ? 'ДА' : 'НЕТ'}</span>
      </div>

      <Button icon={Radio} size="sm" onClick={onRefresh} full>
        Обновить телеметрию
      </Button>

      <Divider label="Загрузка сертификата" />

      <Field
        label="Сертификат пилота / БВС"
        hint="PDF или изображение. Аналог учёта по Part 107."
      >
        <label className="ut-btn cursor-pointer">
          <Upload size={12} aria-hidden />
          {certFile ?? 'Выбрать файл'}
          <input
            type="file"
            className="hidden"
            accept=".pdf,image/*"
            onChange={(e) => setCertFile(e.target.files?.[0]?.name ?? null)}
          />
        </label>
      </Field>

      <div className="rounded-md border border-line px-2 py-1.5 text-[10px] leading-snug text-ink-muted">
        Формат данных — ГОСТ Р 71934-2025. Идентификатор передаётся в СК WGS-84
        (десятичные градусы) на частоте 1090 МГц, мощность излучения не менее 2 Вт.
      </div>
    </div>
  )
}
